// Phase JB.3b — Community Ledger card.
//
// Anon-readable. Renders all kinds: wins, losses, voids. NEVER filters
// out losses (N04 / N06 loss visibility).
//
// Composes HairlineRow + Pill + Badge only.

import type { CSSProperties } from "react";
import { Badge, Eyebrow, HairlineRow, Pill } from "../super-acca";
import { useCommunityLedger, type LedgerEntry, type LedgerKind } from "../../conversion/useCommunityLedger.ts";

const fontBody = "var(--sa-font-body)";
const fontMono = "var(--sa-font-mono)";

const wrapperStyle: CSSProperties = { padding: "12px 48px 24px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

function badgeFor(kind: LedgerKind): { label: string; variant: "success" | "danger" | "ghost" | "info" | "amber" } {
  switch (kind) {
    case "signal_won":
    case "slip_won":
    case "win":
      return { label: "WON", variant: "success" };
    case "signal_lost":
    case "loss":
      return { label: "LOST", variant: "danger" };
    case "signal_void":
      return { label: "VOID", variant: "ghost" };
    case "no_bet":
      return { label: "NO BET", variant: "info" };
    case "milestone":
      return { label: "MILESTONE", variant: "amber" };
    case "founder_note":
      return { label: "FOUNDER", variant: "amber" };
    case "wc_event":
      return { label: "WORLD CUP", variant: "info" };
    default:
      return { label: "ENTRY", variant: "ghost" };
  }
}

export interface CommunityLedgerProps {
  /** Test override. */
  entriesOverride?: LedgerEntry[];
}

export function CommunityLedger({ entriesOverride }: CommunityLedgerProps = {}): JSX.Element {
  const { entries: live, loading } = useCommunityLedger();
  const entries = entriesOverride ?? live;

  if (loading && entriesOverride === undefined) {
    return (
      <section aria-label="Community Ledger" data-testid="community-ledger-loading" style={wrapperStyle}>
        <div style={cardStyle}>
          <span style={{ fontFamily: fontMono, fontSize: 10.5, letterSpacing: "0.22em", color: "var(--sa-fg-muted)" }}>
            LOADING COMMUNITY LEDGER…
          </span>
        </div>
      </section>
    );
  }

  if (entries.length === 0) {
    return (
      <section aria-label="Community Ledger" data-testid="community-ledger-empty" style={wrapperStyle}>
        <div style={cardStyle}>
          <Eyebrow>Community Ledger · Last 30 days</Eyebrow>
          <p style={{ fontFamily: fontBody, fontSize: 14, color: "var(--sa-fg-secondary)", marginTop: 14, lineHeight: 1.7 }}>
            The ledger fills daily after settlement at 22:50 UTC. Wins, losses and voids all show up here — STRIKZA never hides the losses.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Community Ledger" data-testid="community-ledger-populated" style={wrapperStyle}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Community Ledger · Last 30 days</Eyebrow>
          <Pill>OPEN LEDGER</Pill>
        </div>
        <div style={{ marginTop: 14 }}>
          {entries.map((e, i) => {
            const b = badgeFor(e.kind);
            return (
              <HairlineRow key={e.entry_id} first={i === 0} columns="100px 1fr 90px">
                <span style={{ fontFamily: fontMono, fontSize: 10.5, color: "var(--sa-fg-muted)", letterSpacing: "0.12em" }}>
                  {e.at_date}
                </span>
                <span style={{ fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-primary)" }}>
                  {e.headline}
                </span>
                <span style={{ justifySelf: "end" }} data-testid={`community-ledger-badge-${e.kind}`}>
                  <Badge variant={b.variant}>{b.label}</Badge>
                </span>
              </HairlineRow>
            );
          })}
        </div>
        <p style={{ marginTop: 14, fontFamily: fontBody, fontSize: 12.5, color: "var(--sa-fg-muted)", lineHeight: 1.6 }}>
          Wins, losses and voids — every settled outcome shows up here. The ledger is the receipts page.
        </p>
      </div>
    </section>
  );
}
